import { LocalDate } from "@/time"

export type AnnouncementTags = "feature" | "blog" | "podcast" | "new_user" | "media" | "video"

export const announcementTagLabels: Record<AnnouncementTags, string> = {
    "feature": "Feature",
    "blog": "Blog",
    "podcast": "Podcast",
    "new_user": "New User",
    "media": "Media Coverage",
    "video": "Video"
}

interface XertAnnouncement {
    title: string,
    description: string,
    imageUrl: string
    imageAlt: string,
    url: string,
    buttonText? : string,
    tags?: AnnouncementTags[],
}

export interface XertUpdates {
    title?: string,
    id?: string,
    description?: string
    date: Date,
    hidden: boolean,
    announcements: XertAnnouncement[], 
}

/**Converts the updates from the backend into usable updates on the frontend */
export function convertToUpdates(updates: any[]): XertUpdates[] {
    return updates.map((update) => {
        return {
            title: update.title,
            description: update.description,
            date: LocalDate.toDate(update.date.substring(0, 10)),
            hidden: update.hide,
            id: update._id,
            announcements: update.announcements.map((announcement) => {
                return {
                    title: announcement.title,
                    description: announcement.description,
                    tags: announcement.tags,
                    imageUrl: announcement.imageUrl,
                    imageAlt: announcement.imageAlt,
                    buttonText: announcement.buttonText,
                    url: announcement.url,
                }
            })
            
        }
        
    })
}

export const newAnnouncement: XertAnnouncement = {
    title: undefined,
    description: undefined,
    imageUrl: undefined,
    imageAlt: undefined,
    url: undefined,
    buttonText: undefined,
    tags: [],
}

export const newUpdate: XertUpdates = {
    title: undefined,
    description: undefined,
    date: new Date(),
    hidden: false,
    announcements: [{...newAnnouncement}]
}

// Ordered starting at most recent and descending. Add newest entries to the top.
export const xertUpdates: XertUpdates[] = [
    {
        title: "Introducing Xert Forecast and Race AI",
        description: "After years of development and many months of beta testing, we are proud to release one of the most innovative and powerful planning and training systems available today.  Announcing Forecast AI and the new Race AI system.  The bar has been raised.",
        date: new Date("10/04/2024"),
        hidden: false,
        announcements:  [
            {
                title: "Solving the Performance Puzzle",
                description: "What fitness does it take to win a race? .. or not get dropped on the climb our club does on Saturdays? .. or finish strong on an epic gran fondo? .. or return to where I was 2 years ago?  Get answers with Race AI.",
                imageUrl: "/assets/images/xfai/Xert beats world champion.webp",
                imageAlt: "Xert biker beats world champion",
                url: "https://baronbiosys.com/solving-the-performance-puzzle/",
                tags: ["blog"],
            },
            {
                title: "Outcome-Driven Training Plans",
                description: "Training Plans help you generally prepare for a race or an event.  Outcome-driven training plans predict and prepare you to achieve a specific outcome. Announcing Forecast AI.",
                imageUrl: "/assets/images/xfai/Woman Crystal Ball.webp",
                imageAlt: "Biker with crystal ball envisioning victory",
                url: "https://baronbiosys.com/outcome-driven-training-plans/",
                tags: ["blog"],
            },
            {   
                title: "Entering the Performance and Training Multi-Dimensional Universe",
                description: "Xert was the first to model and track multiple dimensions to your training and performance. We've improved and expanded this and solve many of challenges associated with existing systems.  Read more here.",
                imageUrl: "/assets/images/xfai/Road cyclist riding through a surreal, multi-dimensional universe.webp",
                imageAlt: "Road cyclist riding through a surreal, multi-dimensional universe",
                url: "https://baronbiosys.com/entering-the-performance-and-training-multi-dimensional-universe/",
                tags: ["blog"],
            },
            {   
                title: "New Concepts in Measuring Performance",
                description: "The rider with the most power isn't always the fastest.  Neither is the rider with the highest watts/kg.  Checkout this new blog to understand why.",
                imageUrl: "/assets/images/xfai/Measuring Performance Cyclist.webp",
                imageAlt: "Cyclist is riding surrounding by charts and data",
                url: "https://baronbiosys.com/new-concepts-in-measuring-performance/",
                tags: ["blog"],
            },
        ]
    },

    {
        title: "Mastering Xert - Perform Series",
        date: new Date("11/22/2021"),
        description: "In this last of the Mastering Xert series, the Perform  videos, Scott shows you how to use the Xert metrics in real-time on your bike computer and mobile phone, garnering new innovative insights into your performance and training.  With the Xert algorithms crunching numbers during your rides and workouts, the feedback to get will open up entirely new perspectives about you and your ability to perform.",
        hidden: true,
        announcements:  [
            {
                title: "P1 : Real Time MPA",
                description: "Join Scott in the conclusion to the Mastering Xert video series. The first part of the lesson will dive deeper into Maximum Power Available (MPA) and talk about how you can effectively utilize it in real-time on your bike computer or mobile phone.",
                imageUrl: "https://i3.ytimg.com/vi/ZN_nZrHs6Ls/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/ZN_nZrHs6Ls?si=Cn2_4ehzAqKK1G1h",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "P2 : Applied MPA",
                description: "In the second part of the Master Xert - Perform series, Scott discusses how your real-time MPA can be applied in many useful ways while you're out riding or doing a workout. Tune in to learn more about the Xert Segment Hunter, Xert SMART workouts & intervals, and more!",
                imageUrl: "https://i3.ytimg.com/vi/sDhi55xsw84/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/sDhi55xsw84?si=3EclGtUBUTg-uvAW",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "P3 : Xert Specific Metrics",
                description: "In the conclusion to the 'Perform' series videos, Scott talks about some of the Xert-specific data fields, including Focus & Difficulty Score, Fat & Carb utilization, XSS & XEP, as well as the 'Bioshift' Cadence Optimizer. Xert users looking to get a deeper understanding of the metrics you can use while out riding will get a lot out of this video.",
                imageUrl: "https://i3.ytimg.com/vi/Ok6x0Cxh3uY/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/Ok6x0Cxh3uY?si=fkPbZsEKEFBZguKb",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
        ]
    },

    {
        title: "Mastering Xert - Improve Series",
        date: new Date("04/13/2022"),
        description: "In this followup to the Discover series videos, Scott dives into the aspects of Xert designed to help you improve and bring awareness to the improvement process itself. Learn all about concepts such as XSS, Focus, Training Status and find out how to manage settings to get the most out of the Xert Adaptive Training Advisor.",
        hidden: false,
        announcements:  [
            {
                title: "I1 : Quantifying Your Training",
                description: "In the first part of the Mastering Xert - Improve videos, we set the foundation for how Xert measures and quantifies your training, digging into the new concepts and methods unique to the Xert platform that underlie the training recommendations that come from the adaptive training advisor.",
                imageUrl: "https://i3.ytimg.com/vi/pbuvSj-8YRI/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/pbuvSj-8YRI?si=sXfyfgGOnDlx9j_s",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "I2 : The 3P's of Training",
                description: "In the second part of the Master Xert - Improve series, Scott walks you through the three P's of improvement: Progressive Overload, Periodization and Polarization. Learn why understanding these 3 elements are the key to understanding how your training gets structured over time.",
                imageUrl: "https://i3.ytimg.com/vi/8OQm19_NFjc/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/8OQm19_NFjc?si=jKSfw8flJ81ig694",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "I3 : The Adaptive Training Advisor",
                description: "In the third video of the Mastering Xert - Improve series, we provide an explanation for how the Xert Adaptive Training Advisor uses your preferences and historical information to make recommendations on the training you should perform to achieve your goals.",
                imageUrl: "https://i3.ytimg.com/vi/bpceJ62Sn-o/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/bpceJ62Sn-o?si=TslRfgNDdgG7P-43",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
        ]
    },

    {
        title: "Mastering Xert - Discover Series",
        date: new Date("10/19/2021"),
        description: "Join Scott Steele in this 3 part Xert Academy series that lays the foundation for your understanding of the exciting new concepts and ideas only found in the Xert Breakthrough Training System.  Learn all about breakthroughs, MPA and Fitness Signatures - the heart of Xert.",
        hidden: false,
        announcements:  [
            {
                title: "D1 : Intro & Breakthroughs",
                description: "In the first lesson from Xert Academy, Scott discusses how you can use Xert to Discover your Fitness. In this video, learn about 'Fitness Breakthroughs' and learn about some of the features in Xert.",
                imageUrl: "https://i3.ytimg.com/vi/MM902T3ZNnk/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/MM902T3ZNnk?si=GWpDBneRVs0Oaouf",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "D2 : MPA",
                description: "In the second part of the Mastering Xert - Discover lesson, Scott explains the concept of MPA, as well as walking you through the Activity Details page & demonstrating how to do an MPA analysis.",
                imageUrl: "https://i3.ytimg.com/vi/aLffUtw7a68/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/aLffUtw7a68?si=UH0SFKavPdRltYG7",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
            {
                title: "D3 : Fitness Signatures",
                description: "In the conclusion to the first Mastering Xert lesson, Scott talks about Fitness Signatures and why they're important.",
                imageUrl: "https://i3.ytimg.com/vi/bpceJ62Sn-o/maxresdefault.jpg",
                imageAlt: "Xert biker beats world champion",
                url: "https://youtu.be/bpceJ62Sn-o?si=TslRfgNDdgG7P-43",
                tags: ["new_user", "video"],
                buttonText: "Watch Video"
            },
        ]
    },
]

